import React, { useEffect, useState, useMemo, useCallback } from 'react';
import MainView from './MainView';
import { DeviceByGroupStore } from '../../store/devices/DeviceByGroupStore';
import { useAuthStore } from '../../store/auth/auth';
import { useFilesStore } from '../../store/devices/FilesDevicesFromDB';
import { useOwnerStore } from '../../store/owner/useOwnerStore';
import { useCustomMetrics } from '../../hooks/useCustomMetrics';
import './style/style.css';

const Main: React.FC = () => {
    const [currentMenuItem, setCurrentMenuItem] = useState('main');
    const { user, SmartDVRToken } = useAuthStore();
    const { allDevices, fetchAllDevices } = DeviceByGroupStore();
    const { files, fetchFiles } = useFilesStore();
    const { owners, fetchOwners } = useOwnerStore();
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const { trackDeviceOnline, trackDeviceOffline } = useCustomMetrics();

    const fetchAllFiles = useCallback(async () => {
        if (!user?.login || !SmartDVRToken) return;
        setLoadingEvents(true);
        try {
            await fetchFiles({}, SmartDVRToken, user.login, 1, 100, false);
        } finally {
            setLoadingEvents(false);
        }
    }, [fetchFiles, user?.login, SmartDVRToken]);

    useEffect(() => {
        if (user?.login && SmartDVRToken) {
            fetchAllDevices(SmartDVRToken, user.login);
            fetchAllFiles();
            fetchOwners(1, 1000);
        }
    }, [user, SmartDVRToken, fetchAllDevices, fetchAllFiles, fetchOwners]);

    const handleMenuClick = useCallback((key: string) => {
        setCurrentMenuItem(key);
    }, []);

    const onlineDevices = useMemo(() => allDevices.filter((device) => device.online).length, [allDevices]);
    const offlineDevices = useMemo(() => allDevices.length - onlineDevices, [allDevices, onlineDevices]);

    useEffect(() => {
        trackDeviceOnline(onlineDevices);
        trackDeviceOffline(offlineDevices);
    }, [onlineDevices, offlineDevices, trackDeviceOnline, trackDeviceOffline]);

    const deviceTypeCount = useMemo(() => allDevices.reduce(
        (acc, device) => {
            const owner = owners.find((o) => o.UID === device.ownerUID);
            if (!owner) {
                acc.unassigned += 1;
                return acc;
            }
            if (owner.car) {
                acc.cars += 1;
            } else {
                acc.employees += 1;
            }
            return acc;
        },
        { cars: 0, employees: 0, unassigned: 0 }
    ), [allDevices, owners]);

    const deviceTypeData = useMemo(() => {
        const employees = deviceTypeCount.employees;
        const cars = deviceTypeCount.cars;
        const unassigned = deviceTypeCount.unassigned;

        return [
            { type: 'Сотрудник', value: employees },
            { type: 'Автомобиль', value: cars },
            { type: 'Не назначено', value: unassigned },
        ];
    }, [deviceTypeCount.employees, deviceTypeCount.cars, deviceTypeCount.unassigned]);

    const connectionLossEvents = useMemo(() => allDevices
        .filter((device) => !device.online)
        .map((device, index) => ({
            id: -index - 1,
            UID: device.UID,
            deviceUID: device.UID,
            deviceDID: device.DID,
            fileType: 'connection-lost',
            name: 'Потеряна связь с устройством',
            start: device.pulse_time || new Date().toISOString(),
            end: '',
            size: 0,
            duration: 0,
            downloaded: false,
            storagePath: '',
            previewPath: '',
            tryDownloadCount: 0,
            caseNumber: 0,
            caseDescription: '',
            groupUID: device.groupUID,
            ownerUID: device.ownerUID,
            rating: 0,
        })), [allDevices]);

    const allEvents = useMemo(() => {
        const events = [...connectionLossEvents, ...files];
        events.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
        return events;
    }, [connectionLossEvents, files]);

    return (
        <MainView
            currentMenuItem={currentMenuItem}
            handleMenuClick={handleMenuClick}
            onlineDevices={onlineDevices}
            offlineDevices={offlineDevices}
            allDevicesLength={allDevices.length}
            deviceTypeData={deviceTypeData}
            loadingEvents={loadingEvents}
            allEvents={allEvents}
            alertOpen={alertOpen}
            setAlertOpen={setAlertOpen}
        />
    );
};

export default Main;
